import { USER_CONTEXT, SUPER_ADMIN_ROLES, ROLES } from "../../../../constants";

/** Permissions tab: use Freedom `roleName`, not role-type category (System Admin vs Facility Admin share a category). */
function isPermissionsTabVisibleForProfileSubject(freedomRoleName) {
    if (freedomRoleName == null || freedomRoleName === "") return false;
    if (SUPER_ADMIN_ROLES.includes(freedomRoleName)) return false;
    if (freedomRoleName === ROLES.SYSTEM_ADMIN) return false;
    if (freedomRoleName === "Facility Admin") return false;
    return true;
}

/**
 * Tab config for Admin User Profile (access matrix + Permissions).
 *
 * New UI (createStaffV2Enabled):
 * - Superadmin viewing own: Learner Groups only.
 * - Superadmin viewing another Superadmin: no tabs.
 * - Superadmin viewing other roles: Facilities; + Permissions when the subject role supports overrides.
 * - Non-Superadmin: Facilities; + Permissions when applicable; + Learner Groups when viewing own.
 *
 * Legacy UI: Details, Relationship, Roles, + Learner Groups when own, + Communication for Superadmin/Facility Admin.
 */

const TAB_FACILITIES = (allTabs) => ({ id: allTabs.Roles, label: "Facilities", icon: "flag" });
const TAB_LEARNER_GROUPS = (allTabs, icon = null) => ({
    id: allTabs.Learner_Group,
    label: "Learner Groups",
    ...(icon && { icon }),
});
const TAB_PERMISSIONS = (allTabs) => ({ id: allTabs.Permissions, label: "Permissions", icon: "security" });

function buildNewTabs({ allTabs, isViewingOwnProfile, isSuperAdmin, profileIsSuperAdmin, freedomRoleName }) {
    if (isSuperAdmin) {
        if (isViewingOwnProfile) return [TAB_LEARNER_GROUPS(allTabs)];
        if (profileIsSuperAdmin) return [];
        const tabs = [TAB_FACILITIES(allTabs)];
        if (isPermissionsTabVisibleForProfileSubject(freedomRoleName)) {
            tabs.push(TAB_PERMISSIONS(allTabs));
        }
        return tabs;
    }
    const tabs = [TAB_FACILITIES(allTabs)];
    if (isPermissionsTabVisibleForProfileSubject(freedomRoleName)) {
        tabs.push(TAB_PERMISSIONS(allTabs));
    }
    if (isViewingOwnProfile) tabs.push(TAB_LEARNER_GROUPS(allTabs, "group"));
    return tabs;
}

function buildLegacyTabs({ allTabs, isViewingOwnProfile, isSuperAdmin, isFacilityAdmin, isDisableV1MessagingEnabled }) {
    const tabs = [
        { id: allTabs.Details, label: "Details" },
        { id: allTabs.Relationship, label: "Relationship" },
        { id: allTabs.Roles, label: "Roles" },
    ];
    if (isViewingOwnProfile) tabs.push({ id: allTabs.Learner_Group, label: "Learner Groups" });
    if (isSuperAdmin || isFacilityAdmin) tabs.splice(1, 0, { id: allTabs.Communication, label: "Communication" });
    if (isDisableV1MessagingEnabled) {
        return tabs.filter((t) => t.id !== allTabs.Relationship && t.id !== allTabs.Communication);
    }
    return tabs;
}

const useTabDetailsHook = ({
    profileId,
    allTabs,
    global,
    isDisableV1MessagingEnabled,
    freedomRoleName,
    createStaffV2Enabled,
}) => {
    const isViewingOwnProfile = profileId != null && String(profileId) === String(USER_CONTEXT.personId);
    const isSuperAdmin = Boolean(global?.isSuperAdmin);
    const isFacilityAdmin = Boolean(global?.isFacilityAdmin);
    const profileIsSuperAdmin = freedomRoleName != null && SUPER_ADMIN_ROLES.includes(freedomRoleName);

    if (Boolean(createStaffV2Enabled)) {
        return buildNewTabs({
            allTabs,
            isViewingOwnProfile,
            isSuperAdmin,
            profileIsSuperAdmin,
            freedomRoleName,
        });
    }

    return buildLegacyTabs({
        allTabs,
        isViewingOwnProfile,
        isSuperAdmin,
        isFacilityAdmin,
        isDisableV1MessagingEnabled,
    });
};

export default useTabDetailsHook;
